/**
 * Convert Markdown to HTML for backward compatibility
 * Handles basic Markdown elements: headings, bold, italic, lists, links, paragraphs
 */

/**
 * Detects if content is likely Markdown format
 */
export function isMarkdown(content: string): boolean {
  if (!content || content.trim() === '') {
    return false;
  }

  // If content starts with HTML tags, it's likely HTML
  if (content.trim().startsWith('<')) {
    return false;
  }

  // Check for common Markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/m,           // Headings (# ## ###)
    /\*\*.*?\*\*/,           // Bold (**text**)
    /\*[^*].*?\*/,           // Italic (*text*)
    /^[-*+]\s/m,             // Unordered lists
    /^\d+\.\s/m,             // Ordered lists
    /\[.*?\]\(.*?\)/,        // Links [text](url)
  ];

  return markdownPatterns.some(pattern => pattern.test(content));
}

/**
 * Convert Markdown to HTML
 * Handles basic Markdown syntax for policy documents
 */
export function markdownToHtml(markdown: string): string {
  if (!markdown || markdown.trim() === '') {
    return '';
  }

  let html = markdown;

  // Convert headings
  html = html.replace(/^###### (.*$)/gim, '<h6>$1</h6>');
  html = html.replace(/^##### (.*$)/gim, '<h5>$1</h5>');
  html = html.replace(/^#### (.*$)/gim, '<h4>$1</h4>');
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');

  // Convert bold (**text** or __text__)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');

  // Convert italic (*text* or _text_)
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

  // Convert links [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');

  // Convert paragraphs and lists line by line
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let currentList: string[] = [];
  let listType: 'ul' | 'ol' | null = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    // Check for unordered list item
    const ulMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    // Check for ordered list item
    const olMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    
    if (ulMatch) {
      // If we were in an ordered list, close it first
      if (listType === 'ol' && currentList.length > 0) {
        processedLines.push(`<${listType}>${currentList.join('\n')}</${listType}>`);
        currentList = [];
      }
      listType = 'ul';
      currentList.push(`<li>${ulMatch[1]}</li>`);
    } else if (olMatch) {
      // If we were in an unordered list, close it first
      if (listType === 'ul' && currentList.length > 0) {
        processedLines.push(`<${listType}>${currentList.join('\n')}</${listType}>`);
        currentList = [];
      }
      listType = 'ol';
      currentList.push(`<li>${olMatch[1]}</li>`);
    } else {
      // Not a list item - close any open list first
      if (currentList.length > 0 && listType) {
        processedLines.push(`<${listType}>${currentList.join('\n')}</${listType}>`);
        currentList = [];
        listType = null;
      }
      
      // Process the line
      if (!trimmed) {
        processedLines.push('');
      } else if (trimmed.startsWith('<')) {
        // Already HTML (heading, etc.)
        processedLines.push(trimmed);
      } else {
        // Regular text - wrap in paragraph
        processedLines.push(`<p>${trimmed}</p>`);
      }
    }
  }

  // Close any remaining open list
  if (currentList.length > 0 && listType) {
    processedLines.push(`<${listType}>${currentList.join('\n')}</${listType}>`);
  }

  html = processedLines.join('\n');

  // Clean up empty paragraphs
  html = html.replace(/<p><\/p>/g, '');
  
  // Clean up multiple newlines
  html = html.replace(/\n{3,}/g, '\n\n');

  return html.trim();
}

